import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, isAdmin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const user = await login(formData.email, formData.password);
            toast.success("Login berhasil!");
            
            if (user.role === "admin") {
                navigate("/admin");
            } else {
                navigate("/dashboard");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Email atau password salah");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-red-500 to-orange-500 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1590415914114-935b37182a76?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Studio"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative z-10 flex flex-col justify-center p-12 text-white">
                    <Link to="/" className="flex items-center gap-2 mb-12">
                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">mangoplus.ai</span>
                    </Link>
                    <h1 className="font-heading text-4xl font-bold mb-4">
                        Selamat Datang Kembali
                    </h1>
                    <p className="text-xl text-white/80 max-w-md">
                        Masuk untuk melanjutkan membuat foto produk profesional dengan AI.
                    </p>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-heading font-bold text-xl">mangoplus.ai</span>
                        </Link>
                    </div>

                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Masuk</h2>
                    <p className="text-gray-600 mb-8">
                        Belum punya akun?{" "}
                        <Link to="/register" className="text-red-500 hover:text-red-600 font-medium">
                            Daftar gratis
                        </Link>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="nama@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="h-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-red-500"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    className="h-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-red-500 pr-12"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-lg"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Memproses..." : "Masuk"}
                        </Button>
                    </form>

                    <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500 mb-2">Demo Admin Account:</p>
                        <p className="text-sm font-mono text-gray-700">admin@mangoplus.ai / admin123</p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
